/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import { EmitterMixin } from 'ckeditor5/src/utils.js';

import MessagesCompressor from './../messagescompressor';

import AddCommentMessage from './messages/addcommentmessage';
import AddCommentResponse from './responses/addcommentresponse';
import ConnectMessage from './messages/connectmessage';
import ConnectResponse from './responses/connectresponse';
import RemoveCommentMessage from './messages/removecommentmessage';
import RemoveCommentThreadMessage from './messages/removecommentthreadmessage';
import UpdateCommentMessage from './messages/updatecommentmessage';
import GetCommentThreadMessage from './messages/getcommentthreadmessage';
import GetCommentThreadResponse from './responses/getcommentthreadresponse';
import GetDocumentThreadsMessage from './messages/getdocumentthreadsmessage';
import GetDocumentThreadsResponse from './responses/getdocumentthreadsresponse';

import WebSocketGateway, {
	IWebSocketGateway,
	WebSocketGatewayState,
	WEB_SOCKET_GATEWAY_STATES
} from './../websocketgateway/websocketgateway';
import CKEditorCloudServicesError from './../ckeditorcloudserviceserror';
import Sessions from '../sessions/sessions';
import { IThread } from './descriptors/commentsthreaddescriptor';
import Channel from '../websocketgateway/channel';
import SessionCollection from '../sessions/sessioncollection';
import { IComment } from './descriptors/commentdescriptor';
import ServiceNotConnectedError from '../errors/ServiceNotConnectedError';

import type Message from '../message';
import CKEditorCloudServicesServerError from '../ckeditorcloudservicesservererror';
import AddCommentThreadResponse from './responses/comment-threads/addcommentthreadresponse';
import AddCommentThreadMessage from './messages/comment-threads/addcommentthreadmessage';
import ResolveCommentThreadMessage from './messages/comment-threads/resolvecommentthreadmessage';
import ReopenCommentThreadMessage from './messages/comment-threads/reopencommentthreadmessage';
import UpdateCommentThreadMessage from './messages/comment-threads/updatecommentthreadmessage';
import ResolveCommentThreadResponse from './responses/comment-threads/resolvecommentthreadresponse';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _SERVICE: number = 9;

export interface IAddCommentThreadParams {
	commentThreadId: string;
	context?: Record<string, unknown> | null;
	comments?: IComment[];
	resolvedAt?: Date | string | null;
	createdAt?: Date | string | null;
	resolvedBy?: string | null;
	deletedAt?: Date | null | string;
	attributes?: Record<string, unknown> | null;
}

export interface IUpdateCommentThreadParams {
	commentThreadId: string;
	context?: Record<string, unknown> | null;
	attributes?: Record<string, unknown> | null;
	unlinkedAt?: Date | null | string;
}

// eslint-disable-next-line new-cap
class CommentsService extends /* #__PURE__ -- @preserve */ EmitterMixin() {
	private _isConnected: boolean = false;

	private _wsGateway?: IWebSocketGateway;

	private _channel?: Channel;

	private _connectedSessions?: SessionCollection;

	private static readonly _SERVICE: number = _SERVICE;

	public constructor( private readonly _documentId: string ) {
		super();
	}

	public get isConnected(): boolean {
		return this._isConnected;
	}

	/**
	 * Connects to WebSocketGateway and starts listening on channel.
	 */
	public async connect( wsGateway: IWebSocketGateway ): Promise<IThread[] | void> {
		if ( this._isConnected ) {
			return;
		}

		if ( wsGateway.state !== WEB_SOCKET_GATEWAY_STATES.CONNECTED ) {
			throw new CKEditorCloudServicesError( 'WebSocket Gateway is not connected.', wsGateway );
		}

		const connectMessage: ConnectMessage = new ConnectMessage( this._documentId );

		this._wsGateway = wsGateway;
		this.stopListening( wsGateway, 'change:state' );

		const serviceResponse: Buffer = await wsGateway._sendRequest(
			_SERVICE,
			ConnectMessage.TYPE,
			MessagesCompressor.encode( connectMessage )
		);

		const { channel, threads }: ConnectResponse = MessagesCompressor.decode(
			serviceResponse,
			ConnectResponse
		) as ConnectResponse;

		this.listenTo(
			wsGateway,
			'change:state',
			// eslint-disable-next-line @typescript-eslint/naming-convention
			( _0, _1, value ) => this._onWsGatewayStateChange( value as WebSocketGatewayState ),
			{ priority: WebSocketGateway._CHANGE_STATE_EVENT_PRIORITY }
		);

		this._connectToChannel( wsGateway, channel );

		this._isConnected = true;
		this.fire( 'connected' );

		return threads;
	}

	/**
	 * Disconnects CommentsService from CKEditor Cloud Services.
	 */
	public disconnect(): void {
		if ( !this._isConnected ) {
			return;
		}

		this._isConnected = false;

		if ( this._wsGateway ) {
			this.stopListening( this._wsGateway );

			this._wsGateway = undefined;
		}

		if ( this._channel ) {
			this.stopListening( this._channel );

			this._channel = undefined;
		}

		if ( this._connectedSessions ) {
			this._connectedSessions.disconnect();

			this._connectedSessions = undefined;
		}

		this.fire( 'disconnected' );

		this.stopListening();
	}

	public async addComment(
		commentThreadId: string,
		commentId: string,
		content: string,
		attributes?: Record<string, unknown>
	): Promise<AddCommentResponse> {
		const message: AddCommentMessage = new AddCommentMessage(
			this._documentId,
			commentThreadId,
			content,
			commentId,
			undefined,
			undefined,
			attributes
		);

		const responseBuffer: Buffer = await this._sendRequest( AddCommentMessage.TYPE, message );

		return MessagesCompressor.decode( responseBuffer, AddCommentResponse ) as AddCommentResponse;
	}

	public async updateComment(
		commentThreadId: string,
		commentId: string,
		content: string,
		attributes?: Record<string, unknown>
	): Promise<void> {
		const isAttributesUpdated: boolean = typeof attributes !== 'undefined';
		const message: UpdateCommentMessage = new UpdateCommentMessage(
			this._documentId,
			commentThreadId,
			commentId,
			content,
			attributes,
			isAttributesUpdated
		);

		await this._sendRequest( UpdateCommentMessage.TYPE, message );
	}

	public async removeComment( commentThreadId: string, commentId: string ): Promise<void> {
		const message: RemoveCommentMessage = new RemoveCommentMessage( this._documentId, commentThreadId, commentId );

		await this._sendRequest( RemoveCommentMessage.TYPE, message );
	}

	public async removeCommentThread( commentThreadId: string ): Promise<void> {
		const message: RemoveCommentThreadMessage = new RemoveCommentThreadMessage( this._documentId, commentThreadId );

		await this._sendRequest( RemoveCommentThreadMessage.TYPE, message );
	}

	public async addCommentThread( addCommentThreadParams: IAddCommentThreadParams ): Promise<AddCommentThreadResponse> {
		const message: AddCommentThreadMessage = new AddCommentThreadMessage(
			this._documentId,
			addCommentThreadParams.commentThreadId,
			addCommentThreadParams.comments?.map( comment => ( {
				...comment,
				documentId: this._documentId,
				commentThreadId: addCommentThreadParams.commentThreadId
			} ) ),
			addCommentThreadParams.context,
			addCommentThreadParams.resolvedBy,
			addCommentThreadParams.createdAt,
			addCommentThreadParams.resolvedAt,
			addCommentThreadParams.deletedAt,
			addCommentThreadParams.attributes
		);

		const responseBuffer: Buffer = await this._sendRequest( AddCommentThreadMessage.TYPE, message );

		return MessagesCompressor.decode( responseBuffer, AddCommentThreadResponse ) as AddCommentThreadResponse;
	}

	public async resolveCommentThread( commentThreadId: string ): Promise<ResolveCommentThreadResponse> {
		const message: ResolveCommentThreadMessage = new ResolveCommentThreadMessage(
			commentThreadId,
			this._documentId
		);

		const responseBuffer: Buffer = await this._sendRequest( ResolveCommentThreadMessage.TYPE, message );

		return MessagesCompressor.decode( responseBuffer, ResolveCommentThreadResponse ) as ResolveCommentThreadResponse;
	}

	public async reopenCommentThread( commentThreadId: string ): Promise<void> {
		const message: ReopenCommentThreadMessage = new ReopenCommentThreadMessage(
			commentThreadId,
			this._documentId
		);

		await this._sendRequest( ReopenCommentThreadMessage.TYPE, message );
	}

	public async updateCommentThread( params: IUpdateCommentThreadParams ): Promise<void> {
		const message: UpdateCommentThreadMessage = new UpdateCommentThreadMessage(
			params.commentThreadId,
			this._documentId,
			params.context,
			params.attributes,
			params.unlinkedAt
		);

		await this._sendRequest( UpdateCommentThreadMessage.TYPE, message );
	}

	public async getCommentThread(
		commentThreadId: string,
		attempt: number = 1
	): Promise<IThread> {
		const message: GetCommentThreadMessage = new GetCommentThreadMessage( commentThreadId, this._documentId );

		try {
			const responseBuffer: Buffer = await this._sendRequest( GetCommentThreadMessage.TYPE, message );

			const commentThreadResponse: GetCommentThreadResponse = MessagesCompressor.decode(
				responseBuffer,
				GetCommentThreadResponse
			) as GetCommentThreadResponse;

			return {
				commentThreadId: commentThreadResponse.commentThreadId,
				comments: commentThreadResponse.comments,
				attributes: commentThreadResponse.attributes,
				context: commentThreadResponse.context,
				resolvedAt: commentThreadResponse.resolvedAt,
				resolvedBy: commentThreadResponse.resolvedBy,
				deletedAt: commentThreadResponse.deletedAt,
				unlinkedAt: commentThreadResponse.unlinkedAt
			};
		} catch ( error ) {
			if ( error.name === 'CKEditorCloudServicesServerError' && error.code === '404' && attempt < 5 ) {
				await _wait( 100 * attempt );

				return this.getCommentThread( commentThreadId, attempt + 1 );
			}

			if ( error.name === 'CKEditorCloudServicesServerError' ) {
				throw error;
			}

			throw CKEditorCloudServicesServerError.fromPublicError( error );
		}
	}

	public async getDocumentThreads(): Promise<{ threads: IThread[]; }> {
		const message: GetDocumentThreadsMessage = new GetDocumentThreadsMessage( this._documentId );

		const responseBuffer: Buffer = await this._sendRequest( GetDocumentThreadsMessage.TYPE, message );

		const { threads }: GetDocumentThreadsResponse = MessagesCompressor.decode(
			responseBuffer,
			GetDocumentThreadsResponse
		) as GetDocumentThreadsResponse;

		return { threads };
	}

	/**
	 * Returns read-only observable collection with EndUsers Sessions for comments service.
	 */
	public async getConnectedSessions(): Promise<SessionCollection> {
		if ( !this._isConnected ) {
			throw new ServiceNotConnectedError( 'Comments', this );
		}

		if ( !this._connectedSessions ) {
			this._connectedSessions = await Sessions.getConnectedSessions( this._wsGateway!, this._documentId, _SERVICE );
		}

		return this._connectedSessions;
	}

	/**
	 * Starts to listen on a given channel.
	 */
	private _connectToChannel( wsGateway: IWebSocketGateway, channelName: string ): void {
		this._channel = wsGateway._getChannel( CommentsService._SERVICE, channelName );

		if ( !this._channel ) {
			return;
		}

		this.listenTo(
			this._channel,
			this._channel.getEventName( AddCommentMessage.TYPE ),
			( event, data ) => {
				const addCommentMessage: AddCommentMessage = MessagesCompressor.decode( data, AddCommentMessage ) as AddCommentMessage;

				this.fire( 'commentAdded', addCommentMessage );
			}
		);

		this.listenTo(
			this._channel,
			this._channel.getEventName( RemoveCommentMessage.TYPE ),
			( event, data ) => {
				const removeCommentMessage: RemoveCommentMessage = MessagesCompressor.decode(
					data,
					RemoveCommentMessage
				) as RemoveCommentMessage;

				this.fire( 'commentRemoved', removeCommentMessage );
			}
		);

		this.listenTo(
			this._channel,
			this._channel.getEventName( UpdateCommentMessage.TYPE ),
			( event, data ) => {
				const updateCommentMessage: UpdateCommentMessage = MessagesCompressor.decode(
					data,
					UpdateCommentMessage
				) as UpdateCommentMessage;

				this.fire( 'commentUpdated', updateCommentMessage );
			}
		);

		this.listenTo(
			this._channel,
			this._channel.getEventName( RemoveCommentThreadMessage.TYPE ),
			( event, data ) => {
				const removeCommentThreadMessage: RemoveCommentThreadMessage = MessagesCompressor.decode(
					data,
					RemoveCommentThreadMessage
				) as RemoveCommentThreadMessage;

				this.fire( 'commentThreadRemoved', removeCommentThreadMessage );
			}
		);

		this.listenTo(
			this._channel,
			this._channel.getEventName( AddCommentThreadMessage.TYPE ),
			( event, data ) => {
				const addCommentThreadMessage: AddCommentThreadMessage = MessagesCompressor.decode(
					data,
					AddCommentThreadMessage
				) as AddCommentThreadMessage;

				this.fire( 'commentThreadAdded', addCommentThreadMessage );
			}
		);

		this.listenTo(
			this._channel,
			this._channel.getEventName( UpdateCommentThreadMessage.TYPE ),
			( event, data ) => {
				const updateCommentThreadMessage: UpdateCommentThreadMessage = MessagesCompressor.decode(
					data,
					UpdateCommentThreadMessage
				) as UpdateCommentThreadMessage;

				this.fire( 'commentThreadUpdated', updateCommentThreadMessage );
			}
		);

		this.listenTo(
			this._channel,
			this._channel.getEventName( ResolveCommentThreadMessage.TYPE ),
			( event, data ) => {
				const resolveCommentThreadMessage: ResolveCommentThreadMessage = MessagesCompressor.decode(
					data,
					ResolveCommentThreadMessage
				) as ResolveCommentThreadMessage;

				this.fire( 'commentThreadResolved', resolveCommentThreadMessage );
			}
		);

		this.listenTo(
			this._channel,
			this._channel.getEventName( ReopenCommentThreadMessage.TYPE ),
			( event, data ) => {
				const reopenCommentThreadMessage: ReopenCommentThreadMessage = MessagesCompressor.decode(
					data,
					ReopenCommentThreadMessage
				) as ReopenCommentThreadMessage;

				this.fire( 'commentThreadReopened', reopenCommentThreadMessage );
			}
		);
	}

	/**
	 * Processes websocket gateway state changes.
	 */
	private _onWsGatewayStateChange( value: WebSocketGatewayState ): void {
		if ( value === WEB_SOCKET_GATEWAY_STATES.DISCONNECTED ) {
			this.disconnect();
		}
	}

	private _sendRequest( method: string, message: Message ): Promise<Buffer> {
		if ( !this._wsGateway || !this._isConnected ) {
			throw new ServiceNotConnectedError( 'Comments', this );
		}

		return this._wsGateway._sendRequest( _SERVICE, method, MessagesCompressor.encode( message ) );
	}
}

function _wait( time: number ): Promise<void> {
	return new Promise( resolve => {
		setTimeout( resolve, time );
	} );
}

export default CommentsService;
